import { useEffect, useState } from 'react';
import { Backpack, BookOpen, Camera, Hash, ShieldCheck, Tags, Users, UsersRound } from 'lucide-react';
import type { Me } from '../api/auth';
import { rosterApi } from '../api/auth';
import { conversationsApi } from '../api/conversations';
import { subgroupsApi } from '../api/subgroups';
import { eventTypesApi } from '../api/eventTypes';
import { infoApi } from '../api/info';
import { packingApi } from '../api/packing';
import { unitModeration } from '../api/moderation';
import { unitPhotoSettingsApi } from '../api/unitPhotoSettings';
import { photoSettingSummary } from '../lib/photoAudience';
import { AppHeader } from '../ui/AppHeader';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { LinkRow } from '../ui/LinkRow';

/**
 * The unit's lists, in one place — TROOP-120.
 *
 * **Why this exists.** Roster, the subgroups, Kinds of event and What to bring were four buttons
 * under "What's coming up", on the page a family opens to find out when the campout is. They are
 * not what that page is for, and the *Kinds of event* button had carried a comment since it was
 * written saying it belonged in a unit settings area "when one exists". This is that area.
 *
 * **A fact table you can walk into, not a grid of cards.** `DocketRow`'s rule is that a row's
 * value is the answer and not a category — a row saying only "Roster" has moved information behind
 * a tap and called it navigation. So every row here answers the question a volunteer actually
 * arrives with: is the roster up to date, how many patrols are there, has anybody written the
 * packing lists yet. {@link LinkRow} is `FactRow`'s layout for the same reason the docket's is:
 * one column of labels down the left edge is this product's spine, and somebody who has read an
 * event page already knows how to read this one.
 *
 * **The two groups are information, not tidiness.** *Who's in the unit* changes weekly and
 * everybody reads it; *how the unit runs* is set up once a season and mostly read by the software.
 * That split is also where the permissions fall, which is what makes it honest — the first group
 * is offered to everyone and the second is largely officer work.
 *
 * **Any member reaches this, and the controls stay gated on the screens themselves.** Making it
 * officers-only would quietly take the roster away from members, which is an access change wearing
 * a navigation change's clothes. Which patrol a Scout is in is roster information and the roster
 * is already visible.
 *
 * **The counts arrive after the rows, and each one on its own.** Every row renders immediately
 * without its number, so the page is never slower to *use* — only richer as the numbers land. They
 * are deliberately not awaited together: most of these reads come back in under 90ms on the dev
 * troop and the packing one takes nearly two seconds, so a single batch would leave every row
 * showing a placeholder for two seconds and hand back exactly the cost this design claims not to
 * have. A count that fails is simply absent rather than a zero, because "0 patrols" and "we could
 * not ask" are different statements and only one of them is true.
 */

/** What the rows' own reads came back with. Undefined means not in yet; null means it failed. */
interface Counts {
  people?: number | null;
  subgroups?: number | null;
  eventTypes?: number | null;
  lists?: number | null;
  channels?: number | null;
  /** Open reports (TROOP-12). Zero is a real and reassuring answer here, unlike the others. */
  openReports?: number | null;
  /**
   * Not a count: what the unit has decided about photographs, already phrased (TROOP-225). "On ·
   * anyone can add", or "Off". The row's value is the answer, and the answer here is a sentence
   * rather than a number — the same rule, a different shape.
   */
  photos?: string | null;
  /** Sections on the unit's Info page (TROOP-256). Zero is a real answer, said in words. */
  infoSections?: number | null;
}

export function ManageScreen({
  unitId,
  unitName,
  subgroupLabelSingular,
  subgroupLabelPlural,
  canManageChannels,
  me,
  onNavigate,
}: {
  unitId: string;
  unitName: string;
  /**
   * The unit's own words — "Patrol"/"Patrols" or "Den"/"Dens". Never a literal on this screen
   * (rule 16), and **both** are taken rather than the plural being singularised here: `Unit`'s
   * constructor populates the pair, and a `replace(/s$/, '')` would turn a Cub pack's "Sixes"
   * into "Sixe". The data already answers this; deriving it is re-implementing the rule.
   */
  subgroupLabelSingular: string;
  subgroupLabelPlural: string;
  /**
   * Whether this person may turn the unit's standing channels on and off. Officers and above; the
   * row is absent rather than disabled for everybody else, because the route behind it bounces a
   * member and a greyed control would owe an explanation that "you are not an officer" does not
   * make interesting.
   */
  canManageChannels: boolean;
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const [counts, setCounts] = useState<Counts>({});

  useEffect(() => {
    let alive = true;

    // Each read lands on its own, and that is not a micro-optimisation. On the dev troop most of
    // these come back in under 90ms and the packing one takes nearly two seconds, so a single
    // `Promise.all` would hold every row hostage to the slowest and leave the page showing
    // placeholders for two seconds -- which is the cost this design claimed not to have. Settled
    // individually, most of the page is answering almost immediately.
    //
    // No number is stated for how many there are, deliberately: this list has grown three times
    // and a count in the prose beside it is the thing that goes quietly stale (TROOP-225).
    //
    // A failure sets null rather than leaving undefined, because "we could not ask" and "not in
    // yet" are different states and only the second one should keep looking like it is loading.
    const settle = <T,>(promise: Promise<T>, onValue: (value: T) => Counts) =>
      promise
        .then((value) => {
          if (alive) setCounts((current) => ({ ...current, ...onValue(value) }));
        })
        .catch(() => undefined);

    // No youth/adult breakdown on the roster row, deliberately. It would have to name the unit's
    // own word for its young members (rule 16) and `/auth/me` does not carry that pair, so the
    // clause would drag an API change into a navigation ticket to say something the roster itself
    // says on arrival.
    void settle(rosterApi.list(unitId), (rows) => ({ people: rows.length }));

    // Retired groups are excluded: they keep their threads and their history, so they are not
    // gone, but "8 patrols" must mean the eight a Scout could be put in today.
    void settle(subgroupsApi.list(unitId), (rows) => ({
      subgroups: rows.filter((s) => !s.isRetired).length,
    }));

    void settle(eventTypesApi.list(unitId), (rows) => ({ eventTypes: rows.length }));
    void settle(infoApi.page(unitId), (page) => ({ infoSections: page.sections.length }));
    void settle(packingApi.templates(unitId), (rows) => ({ lists: rows.length }));

    // What this returns depends entirely on who is asking -- the queue for an adult officer, your
    // own reports for everybody else -- so the count is honest for both without this screen
    // knowing which reader it has. That is the policy answering per row, not a permission check
    // duplicated here.
    void settle(unitModeration(unitId).flags(), (rows) => ({
      openReports: rows.filter((r) => r.status === 'Open').length,
    }));

    // Asked for every member, not only the officers who can change it: whether the unit keeps
    // photos and who adds them is what somebody wants to know before they reach for their camera,
    // and the screen behind this row renders facts to a reader and controls to a writer.
    void settle(unitPhotoSettingsApi.get(unitId), (row) => ({
      photos: photoSettingSummary(row.photosEnabled, row.uploadAudience),
    }));

    // Only asked for when the row that shows it will render. A member cannot reach the screen
    // behind it, so counting for them is a request nobody will ever see the answer to.
    if (canManageChannels) {
      void settle(conversationsApi.channels(unitId), (page) => ({ channels: page.rows.length }));
    }

    return () => {
      alive = false;
    };
  }, [unitId, canManageChannels]);

  return (
    <Page>
      <AppHeader me={me} currentUnitId={unitId} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p className="t-eyebrow" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              {unitName}
            </p>
            <h1
              className="t-heading"
              style={{
                color: 'var(--color-text-primary)',
                margin: 'var(--space-2) 0 var(--space-2)',
              }}
            >
              Manage
            </h1>
            <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
              Everything the unit keeps a list of. Anyone can look; your officers do the changing.
            </p>
          </div>
        </Gutter>
      </section>

      <Gutter>
        <div className="flex flex-col" style={{ paddingBlock: 'var(--space-6)', gap: 'var(--space-7)' }}>
          <Group title="Who's in the unit">
            <LinkRow
              label="Roster"
              icon={Users}
              value={count(counts.people, 'person', 'people')}
              secondary="Add people, set positions, invite guardians."
              onClick={() => onNavigate(`/units/${unitId}/roster`)}
            />

            <LinkRow
              label={subgroupLabelPlural}
              icon={UsersRound}
              // The unit's own words, lowercased into the sentence -- a Pack reads "3 dens".
              value={count(
                counts.subgroups,
                subgroupLabelSingular.toLowerCase(),
                subgroupLabelPlural.toLowerCase(),
              )}
              secondary="Who is in each one, and the thread that comes with it."
              onClick={() => onNavigate(`/units/${unitId}/subgroups`)}
            />
          </Group>

          <Group title="How the unit runs">
            {/* TROOP-256. Offered to every member: anybody reads it, and the screen behind it
                draws its controls from the server's canEdit rather than from this row. */}
            <LinkRow
              label="Info"
              icon={BookOpen}
              value={infoValue(counts.infoSections)}
              secondary="What families ask every year: where you meet, dues, uniform, who to call."
              onClick={() => onNavigate(`/units/${unitId}/info`)}
            />

            <LinkRow
              label="Events"
              icon={Tags}
              value={count(counts.eventTypes, 'kind of event', 'kinds of event')}
              secondary="What a campout brings with it: colour, packing lists, what it asks of families."
              onClick={() => onNavigate(`/units/${unitId}/event-types`)}
            />

            <LinkRow
              label="Bring"
              icon={Backpack}
              value={count(counts.lists, 'list', 'lists')}
              secondary="Saved lists to publish onto an event. Your own are here too, and they follow you."
              onClick={() => onNavigate(`/units/${unitId}/packing-lists`)}
            />

            {/* TROOP-225. The row's value is the unit's answer -- "On, anyone can add" -- and
                never the category, which is what would make this navigation pretending to be
                information. It carries the switch as well as the audience, because until this
                ticket `Unit.PhotosEnabled` had no control anywhere in the product. */}
            <LinkRow
              label="Photos"
              icon={Camera}
              value={counts.photos ?? 'Photos'}
              secondary="Whether the unit keeps photos of its events, and who adds them."
              onClick={() => onNavigate(`/units/${unitId}/photo-settings`)}
            />

            {/* Offered to every member, not gated to officers -- the same reasoning this whole
                screen rests on. A reporter needs somewhere to find out whether anybody looked at
                what they raised (§5), and that is the same screen a reviewer works the queue on.
                The row's own number tells each of them the truth about their own list. */}
            <LinkRow
              label="Reports"
              icon={ShieldCheck}
              value={reportsValue(counts.openReports)}
              secondary="Messages somebody raised a concern about, and what came of them."
              onClick={() => onNavigate(`/units/${unitId}/reports`)}
            />

            {/* Moved off the conversations page (TROOP-122). Choosing which standing channels a
                unit keeps is a once-a-season act, and it was sitting beside the control somebody
                presses to say something today. */}
            {canManageChannels && (
              <LinkRow
                label="Channels"
                icon={Hash}
                value={count(counts.channels, 'channel', 'channels')}
                secondary="The standing threads your unit keeps. Turning one off archives it, readable."
                onClick={() => onNavigate(`/units/${unitId}/channels`)}
              />
            )}
          </Group>
        </div>
      </Gutter>

      <Footer />
    </Page>
  );
}

/**
 * "93 people", or the plain noun while the count is still in flight.
 *
 * Never "0 people" for a failed read: an absent number and a number that is genuinely zero are
 * different statements, and only one of them is something a leader should act on.
 */
function count(value: number | null | undefined, singular: string, plural: string): string {
  if (value == null) {
    // Sentence case, and capitalised because it stands alone as the row's value.
    return plural.charAt(0).toUpperCase() + plural.slice(1);
  }

  return `${value} ${value === 1 ? singular : plural}`;
}

/**
 * "2 waiting", or "Nothing waiting" — and never "0 waiting".
 *
 * Zero is the answer a leader most wants and the one `count` cannot say well: it renders the plain
 * noun while a read is in flight, so a genuine zero would be indistinguishable from loading. Here
 * the two states are worth separating, because "nothing has been reported" is reassurance and "we
 * have not asked yet" is not.
 */
function reportsValue(open: number | null | undefined): string {
  if (open == null) {
    return 'Reports';
  }

  return open === 0 ? 'Nothing waiting' : `${open} waiting for an answer`;
}

/** "3 sections", or "Nothing written yet" -- never "0 sections", which reads as a failed count. */
function infoValue(sections: number | null | undefined): string {
  if (sections == null) {
    return 'Info';
  }

  return sections === 0 ? 'Nothing written yet' : count(sections, 'section', 'sections');
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2
        className="t-eyebrow"
        style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-2)' }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}
